import React, { useEffect, useMemo, useState } from "react";
import { api, dateFR, money } from "@/lib/api";
import { isoDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Wrench, Pencil, Plus, FileDown, Trash2, Car, Wallet } from "lucide-react";
import { toast } from "sonner";
import Plate from "@/components/Plate";
import { generateWorkOrderPDF } from "@/lib/pdf";

const empty = {
  vente_id: "", date: isoDate(),
  type: "Entretien", description: "", pieces: "", main_oeuvre: "",
  statut: "a_faire", mecanicien: "", notes: "",
};

const emptyVO = {
  plaque: "", marque: "", modele: "", annee: "",
  kilometrage: "", couleur: "", client_nom: "", notes: "",
};

const statusBadge = {
  a_faire: { txt: "À faire", cls: "bg-slate-100 text-slate-700 border border-slate-200" },
  en_cours: { txt: "En cours", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  termine: { txt: "Terminée", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
};

export default function Atelier() {
  const [interventions, setInterventions] = useState([]);
  const [ventes, setVentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statutFilter, setStatutFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [voOpen, setVoOpen] = useState(false);
  const [voForm, setVoForm] = useState(emptyVO);
  const [savingVO, setSavingVO] = useState(false);

  // Payment dialog (atelier can register payments for interventions)
  const [payOpen, setPayOpen] = useState(false);
  const [payIntervention, setPayIntervention] = useState(null);
  const [payForm, setPayForm] = useState({
    montant: "", methode: "Espèces", date_paiement: isoDate(), notes: "",
  });
  const [savingPay, setSavingPay] = useState(false);

  const load = async () => {
    setLoading(true);
    // include_atelier=true so workshop-only vehicles are listed alongside sales vehicles
    const [i, v] = await Promise.all([
      api.get("/interventions"),
      api.get("/ventes", { params: { include_atelier: true } }),
    ]);
    setInterventions(i.data);
    setVentes(v.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const ventesMap = useMemo(() => {
    const m = {};
    ventes.forEach((v) => { m[v.id] = v; });
    return m;
  }, [ventes]);

  const filtered = useMemo(() => {
    if (statutFilter === "all") return interventions;
    if (statutFilter === "a_encaisser") {
      return interventions.filter((it) => !it.paye && (it.cout_total || 0) > 0);
    }
    if (statutFilter === "paye") {
      return interventions.filter((it) => it.paye);
    }
    return interventions.filter((it) => it.statut === statutFilter);
  }, [interventions, statutFilter]);

  const counts = useMemo(() => ({
    a_faire: interventions.filter((it) => it.statut === "a_faire").length,
    en_cours: interventions.filter((it) => it.statut === "en_cours").length,
    termine: interventions.filter((it) => it.statut === "termine").length,
    a_encaisser: interventions.filter((it) => !it.paye && (it.cout_total || 0) > 0).length,
    paye: interventions.filter((it) => it.paye).length,
  }), [interventions]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      vente_id: it.vente_id || "",
      date: it.date || "",
      type: it.type || "Entretien",
      description: it.description || "",
      pieces: it.pieces ?? "",
      main_oeuvre: it.main_oeuvre ?? "",
      statut: it.statut || "a_faire",
      mecanicien: it.mecanicien || "",
      notes: it.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vente_id) { toast.error("Sélectionnez un véhicule"); return; }
    try {
      const payload = {
        vente_id: form.vente_id,
        date: form.date,
        type: form.type,
        description: form.description,
        pieces: Number(form.pieces || 0),
        main_oeuvre: Number(form.main_oeuvre || 0),
        statut: form.statut,
        mecanicien: form.mecanicien,
        notes: form.notes,
      };
      if (editing) {
        const { vente_id, ...rest } = payload;
        await api.put(`/interventions/${editing.id}`, rest);
        toast.success("Intervention mise à jour");
      } else {
        await api.post("/interventions", payload);
        toast.success("Intervention créée");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const quickStatus = async (it, newStatut) => {
    try {
      await api.put(`/interventions/${it.id}`, { statut: newStatut });
      toast.success(`Statut → ${statusBadge[newStatut]?.txt || newStatut}`);
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (it) => {
    if (!window.confirm("Supprimer cette intervention ?")) return;
    await api.delete(`/interventions/${it.id}`);
    toast.success("Supprimé");
    load();
  };

  const openPayDialog = (it) => {
    const v = ventesMap[it.vente_id];
    setPayIntervention(it);
    setPayForm({
      montant: String(it.cout_total || ""),
      methode: "Espèces",
      date_paiement: isoDate(),
      notes: v ? `Atelier · ${it.type} · ${v.plaque}` : `Atelier · ${it.type}`,
    });
    setPayOpen(true);
  };

  const submitPay = async (e) => {
    e.preventDefault();
    if (!payIntervention) return;
    const v = ventesMap[payIntervention.vente_id];
    if (!v) {
      toast.error("Véhicule introuvable");
      return;
    }
    setSavingPay(true);
    try {
      const { data: paiement } = await api.post("/paiements", {
        vente_id: v.id,
        client_nom: v.client_nom || "",
        montant: Number(payForm.montant || 0),
        methode: payForm.methode,
        date_paiement: payForm.date_paiement,
        notes: payForm.notes,
      });
      // Mark the intervention as paid + remember the linked payment ID
      try {
        await api.put(`/interventions/${payIntervention.id}`, {
          paye: true,
          linked_paiement_id: paiement?.id || null,
        });
      } catch (err) {
        console.warn("Failed to flag intervention as paid:", err);
      }
      toast.success("Paiement enregistré · Intervention marquée PAYÉE");
      setPayOpen(false);
      setPayIntervention(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'enregistrement");
    } finally {
      setSavingPay(false);
    }
  };

  const submitVO = async (e) => {
    e.preventDefault();
    if (!voForm.plaque.trim() || !voForm.marque.trim() || !voForm.modele.trim()) {
      toast.error("Plaque, marque et modèle obligatoires");
      return;
    }
    setSavingVO(true);
    try {
      const payload = {
        client_nom: voForm.client_nom.trim() || "Atelier",
        marque: voForm.marque.trim(),
        modele: voForm.modele.trim(),
        plaque: voForm.plaque.trim(),
        annee: voForm.annee ? Number(voForm.annee) : null,
        kilometrage: voForm.kilometrage ? Number(voForm.kilometrage) : 0,
        couleur: voForm.couleur.trim(),
        notes: voForm.notes.trim(),
        prix_vente: 0,
        atelier_only: true,
        atelier_statut: "entre",
      };
      const { data } = await api.post("/ventes", payload);
      toast.success("VO atelier ajouté");
      setVoOpen(false);
      setVoForm(emptyVO);
      // Pre-select the new vehicle for a new intervention
      setEditing(null);
      setForm({ ...empty, vente_id: data.id });
      setOpen(true);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'ajout du VO");
    } finally {
      setSavingVO(false);
    }
  };

  const printVenteWO = async (venteId) => {
    const v = ventesMap[venteId];
    if (!v) return;
    const inters = interventions.filter((i) => i.vente_id === venteId);
    generateWorkOrderPDF(v, inters);
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold flex items-center gap-2">
            <Wrench size={12} /> Atelier
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Interventions mécaniques</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Pilotez les travaux atelier sur l'ensemble des véhicules. Filtrez par statut, mettez à jour rapidement.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={voOpen} onOpenChange={(o) => { setVoOpen(o); if (!o) setVoForm(emptyVO); }}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="atelier-add-vo-btn"
                className="rounded-sm border-klein text-klein hover:bg-klein/5"
              >
                <Car size={14} className="mr-1" /> Ajouter un VO atelier
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">Nouveau VO atelier</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-muted-foreground -mt-1">
                Pour ajouter un véhicule qui n'est pas en vente (entretien client, service uniquement).
              </p>
              <form onSubmit={submitVO} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Plaque *</Label>
                    <Input
                      data-testid="atelier-vo-plaque"
                      required
                      autoFocus
                      value={voForm.plaque}
                      onChange={(e) => setVoForm({ ...voForm, plaque: e.target.value.toUpperCase() })}
                      className="mt-1 rounded-sm font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Client (atelier)</Label>
                    <Input
                      data-testid="atelier-vo-client"
                      value={voForm.client_nom}
                      placeholder="Nom du client"
                      onChange={(e) => setVoForm({ ...voForm, client_nom: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Marque *</Label>
                    <Input
                      data-testid="atelier-vo-marque"
                      required
                      value={voForm.marque}
                      onChange={(e) => setVoForm({ ...voForm, marque: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Modèle *</Label>
                    <Input
                      data-testid="atelier-vo-modele"
                      required
                      value={voForm.modele}
                      onChange={(e) => setVoForm({ ...voForm, modele: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Année</Label>
                    <Input
                      data-testid="atelier-vo-annee"
                      type="number" inputMode="decimal"
                      value={voForm.annee}
                      onChange={(e) => setVoForm({ ...voForm, annee: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Kilométrage</Label>
                    <Input
                      data-testid="atelier-vo-km"
                      type="number" inputMode="decimal"
                      value={voForm.kilometrage}
                      onChange={(e) => setVoForm({ ...voForm, kilometrage: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Couleur</Label>
                    <Input
                      data-testid="atelier-vo-couleur"
                      value={voForm.couleur}
                      onChange={(e) => setVoForm({ ...voForm, couleur: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Notes</Label>
                  <Textarea
                    data-testid="atelier-vo-notes"
                    value={voForm.notes}
                    onChange={(e) => setVoForm({ ...voForm, notes: e.target.value })}
                    rows={2}
                    className="mt-1 rounded-sm"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={savingVO}
                    data-testid="atelier-vo-submit"
                    className="bg-klein hover:bg-blue-800 text-white rounded-sm"
                  >
                    {savingVO ? "Ajout…" : "Ajouter et créer intervention"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} data-testid="atelier-add-btn" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                <Plus size={14} className="mr-1" /> Nouvelle intervention
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Modifier l'intervention" : "Nouvelle intervention"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              {!editing && (
                <div>
                  <Label className="text-xs uppercase tracking-wider">Véhicule *</Label>
                  <Select value={form.vente_id || undefined} onValueChange={(v) => setForm({ ...form, vente_id: v })}>
                    <SelectTrigger data-testid="atelier-form-vehicle" className="rounded-sm mt-1"><SelectValue placeholder="Choisir un véhicule…" /></SelectTrigger>
                    <SelectContent>
                      {ventes.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.plaque} · {v.marque} {v.modele}
                          {v.atelier_only ? " · [ATELIER]" : ` — ${v.client_nom || ""}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Date</Label>
                  <Input data-testid="atelier-form-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Entretien">Entretien</SelectItem>
                      <SelectItem value="Réparation">Réparation</SelectItem>
                      <SelectItem value="Contrôle">Contrôle</SelectItem>
                      <SelectItem value="Diagnostic">Diagnostic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Description *</Label>
                <Textarea data-testid="atelier-form-desc" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 rounded-sm" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Pièces (€)</Label>
                  <Input type="number" inputMode="decimal" step="0.01" value={form.pieces} onChange={(e) => setForm({ ...form, pieces: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">M. œuvre (€)</Label>
                  <Input type="number" inputMode="decimal" step="0.01" value={form.main_oeuvre} onChange={(e) => setForm({ ...form, main_oeuvre: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Statut</Label>
                  <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="a_faire">À faire</SelectItem>
                      <SelectItem value="en_cours">En cours</SelectItem>
                      <SelectItem value="termine">Terminée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Mécanicien</Label>
                <Input value={form.mecanicien} onChange={(e) => setForm({ ...form, mecanicien: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="atelier-form-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">{editing ? "Enregistrer" : "Créer"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { v: "all", label: `Toutes (${interventions.length})`, accent: "" },
          { v: "a_faire", label: `À faire (${counts.a_faire})`, accent: "bg-slate-100 text-slate-700" },
          { v: "en_cours", label: `En cours (${counts.en_cours})`, accent: "bg-amber-50 text-amber-700" },
          { v: "termine", label: `Terminées (${counts.termine})`, accent: "bg-emerald-50 text-emerald-700" },
          { v: "a_encaisser", label: `À encaisser (${counts.a_encaisser})`, accent: "bg-red-50 text-red-700" },
          { v: "paye", label: `Payées (${counts.paye})`, accent: "bg-emerald-100 text-emerald-800" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setStatutFilter(t.v)}
            data-testid={`atelier-filter-${t.v}`}
            className={`px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${
              statutFilter === t.v
                ? "bg-klein text-white"
                : `border border-border ${t.accent || "bg-card"} hover:bg-slate-50`
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Actions</th>
              <th className="px-6 py-3">Date</th>
              <th className="px-6 py-3">Plaque · Véhicule</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Description</th>
              <th className="px-6 py-3">Mécanicien</th>
              <th className="px-6 py-3 text-right">Total</th>
              <th className="px-6 py-3 text-center">Statut</th>
            </tr>
          </thead>
          <tbody data-testid="atelier-table-body">
            {loading && <tr><td colSpan={8} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-10 text-center text-muted-foreground">Aucune intervention.</td></tr>
            )}
            {filtered.map((it) => {
              const v = ventesMap[it.vente_id];
              const cfg = statusBadge[it.statut] || statusBadge.a_faire;
              return (
                <tr key={it.id} className="border-t hover:bg-slate-50">
                  <td className="px-6 py-3 whitespace-nowrap">
                    <button
                      onClick={() => openEdit(it)}
                      title="Modifier"
                      data-testid={`atelier-edit-${it.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-klein/5 hover:bg-klein/10 text-klein border border-klein/20 rounded-sm mr-1 font-semibold"
                    >
                      <Pencil size={12} /> Modifier
                    </button>
                    {it.statut === "a_faire" && (
                      <button onClick={() => quickStatus(it, "en_cours")} data-testid={`atelier-start-${it.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-sm mr-1">
                        Démarrer
                      </button>
                    )}
                    {it.statut === "en_cours" && (
                      <button onClick={() => quickStatus(it, "termine")} data-testid={`atelier-finish-${it.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-900 rounded-sm mr-1">
                        Terminer
                      </button>
                    )}
                    {it.cout_total > 0 && v && !it.paye && (
                      <button
                        onClick={() => openPayDialog(it)}
                        title="Encaisser cette intervention"
                        data-testid={`atelier-encaisser-${it.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm mr-1 font-semibold"
                      >
                        <Wallet size={12} /> Encaisser
                      </button>
                    )}
                    {it.paye && (
                      <>
                        <span
                          data-testid={`atelier-paye-${it.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm mr-1 font-bold uppercase tracking-wider"
                        >
                          <Wallet size={12} /> Payée
                        </span>
                        <button
                          onClick={async () => {
                            if (!window.confirm("Annuler le marquage 'PAYÉE' ?\n\n⚠️ Le paiement créé lors de l'encaissement sera également SUPPRIMÉ de la liste Paiements (action irréversible).")) return;
                            try {
                              const { data } = await api.post(`/interventions/${it.id}/unpay`);
                              if (data.deleted_payment) {
                                toast.success("Marquage annulé · Paiement supprimé");
                              } else {
                                toast.success("Marquage annulé (aucun paiement lié à supprimer)");
                              }
                              load();
                            } catch (err) {
                              console.warn("Annulation paye failed:", err);
                              toast.error("Erreur");
                            }
                          }}
                          title="Annuler le marquage 'Payée' et supprimer le paiement"
                          data-testid={`atelier-unpaye-${it.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-red-50 text-red-600 rounded-sm mr-1"
                        >
                          Annuler
                        </button>
                      </>
                    )}
                    {v && (
                      <button onClick={() => printVenteWO(it.vente_id)} title="Fiche atelier PDF" className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-slate-100 rounded-sm mr-1">
                        <FileDown size={12} />
                      </button>
                    )}
                    <button onClick={() => remove(it)} title="Supprimer" className="inline-flex items-center gap-1 px-2 py-1 text-xs text-destructive hover:bg-red-50 rounded-sm">
                      <Trash2 size={12} />
                    </button>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">{dateFR(it.date)}</td>
                  <td className="px-6 py-3">
                    {v ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Plate value={v.plaque} small />
                        <span>{v.marque} {v.modele}</span>
                        {v.atelier_only && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-klein/10 text-klein border border-klein/20">
                            Atelier
                          </span>
                        )}
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-6 py-3">{it.type}</td>
                  <td className="px-6 py-3 max-w-md">{it.description}</td>
                  <td className="px-6 py-3 text-muted-foreground">{it.mecanicien || "—"}</td>
                  <td className="px-6 py-3 text-right font-mono font-semibold">{money(it.cout_total)}</td>
                  <td className="px-6 py-3 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider ${cfg.cls}`}>
                      {cfg.txt}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Payment dialog for atelier */}
      <Dialog open={payOpen} onOpenChange={(o) => { setPayOpen(o); if (!o) setPayIntervention(null); }}>
        <DialogContent className="max-w-md" data-testid="atelier-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Encaisser l'intervention</DialogTitle>
          </DialogHeader>
          {payIntervention && (
            <p className="text-xs text-muted-foreground -mt-2">
              {payIntervention.type} · <strong>{money(payIntervention.cout_total)}</strong>
              {ventesMap[payIntervention.vente_id] && (
                <> · {ventesMap[payIntervention.vente_id].plaque} · {ventesMap[payIntervention.vente_id].client_nom}</>
              )}
            </p>
          )}
          <form onSubmit={submitPay} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Montant *</Label>
                <Input
                  data-testid="atelier-pay-montant"
                  type="number" inputMode="decimal"
                  step="0.01"
                  required
                  value={payForm.montant}
                  onChange={(e) => setPayForm({ ...payForm, montant: e.target.value })}
                  className="mt-1 rounded-sm"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Méthode</Label>
                <Select value={payForm.methode} onValueChange={(v) => setPayForm({ ...payForm, methode: v })}>
                  <SelectTrigger data-testid="atelier-pay-methode" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Espèces">Espèces</SelectItem>
                    <SelectItem value="Virement">Virement</SelectItem>
                    <SelectItem value="Chèque">Chèque</SelectItem>
                    <SelectItem value="Carte">Carte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Date</Label>
              <Input
                data-testid="atelier-pay-date"
                type="date"
                value={payForm.date_paiement}
                onChange={(e) => setPayForm({ ...payForm, date_paiement: e.target.value })}
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Détail / Notes</Label>
              <Textarea
                data-testid="atelier-pay-notes"
                value={payForm.notes}
                onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                rows={2}
                className="mt-1 rounded-sm"
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={savingPay}
                data-testid="atelier-pay-submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
              >
                {savingPay ? "Enregistrement…" : "Encaisser"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
